import { Component, EventEmitter, Input, Output } from '@angular/core';
import { QrCodeRecord } from './app.types';

@Component({
  selector: 'app-saved-qr-code-picker',
  standalone: false,
  template: `
    <section class="saved-qr-picker">
      <label class="saved-qr-picker__label" [for]="searchInputId">{{ label }}</label>
      <input
        [id]="searchInputId"
        class="saved-qr-picker__search"
        type="search"
        [value]="searchQuery"
        [placeholder]="placeholder"
        (input)="emitSearch($event)"
      >

      <ion-note color="medium" *ngIf="isLoading">
        <ion-spinner name="crescent"></ion-spinner>
        Loading QR codes...
      </ion-note>
      <ion-note color="medium" *ngIf="!isLoading && qrCodes.length === 0">{{ emptyMessage }}</ion-note>

      <div class="saved-qr-picker__results" *ngIf="qrCodes.length > 0">
        <button
          type="button"
          class="saved-qr-picker__result"
          [class.active]="selectedQrCodeId === qrCode.id"
          *ngFor="let qrCode of qrCodes"
          (click)="selectQrCode.emit(qrCode)"
        >
          <span>{{ qrCode.title || qrCode.redirect_path }}</span>
          <small>{{ qrCode.target_url }}</small>
        </button>
      </div>

      <div class="saved-qr-picker__pagination" *ngIf="totalPages > 1">
        <ion-button type="button" size="small" fill="outline" [disabled]="isLoading || page <= 1" (click)="pageChange.emit(page - 1)">
          Previous
        </ion-button>
        <span>Page {{ page }} of {{ totalPages }}</span>
        <ion-button type="button" size="small" fill="outline" [disabled]="isLoading || page >= totalPages" (click)="pageChange.emit(page + 1)">
          Next
        </ion-button>
      </div>
    </section>
  `,
  styles: [`
    .saved-qr-picker {
      display: grid;
      gap: 8px;
      padding: 8px 0;
    }

    .saved-qr-picker__label {
      color: var(--ion-color-medium);
      font-size: 0.82rem;
    }

    .saved-qr-picker__search {
      width: 100%;
      min-height: 42px;
      border: 1px solid var(--ion-color-medium);
      border-radius: 6px;
      padding: 8px 10px;
      background: var(--ion-background-color);
      color: var(--ion-text-color);
    }

    .saved-qr-picker ion-note {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .saved-qr-picker ion-spinner {
      width: 16px;
      height: 16px;
    }

    .saved-qr-picker__results {
      display: grid;
      gap: 6px;
      max-height: 260px;
      overflow: auto;
    }

    .saved-qr-picker__result {
      border: 1px solid var(--ion-color-light-shade);
      border-radius: 6px;
      background: var(--ion-color-light);
      color: var(--ion-color-dark);
      display: grid;
      gap: 2px;
      padding: 8px 10px;
      text-align: left;
    }

    .saved-qr-picker__result.active {
      border-color: var(--ion-color-primary);
      box-shadow: 0 0 0 2px rgba(var(--ion-color-primary-rgb), 0.16);
    }

    .saved-qr-picker__result small {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      color: var(--ion-color-medium);
    }

    .saved-qr-picker__pagination {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 8px;
      color: var(--ion-color-medium);
      font-size: 0.82rem;
    }
  `]
})
export class SavedQrCodePickerComponent {
  @Input() label = 'Existing QR Code';
  @Input() placeholder = 'Search QR codes';
  @Input() emptyMessage = 'No saved QR codes match this search.';
  @Input() qrCodes: QrCodeRecord[] = [];
  @Input() selectedQrCodeId: number | null = null;
  @Input() searchQuery = '';
  @Input() isLoading = false;
  @Input() page = 1;
  @Input() totalPages = 1;
  @Input() searchInputId = 'saved-qr-code-search';

  @Output() selectQrCode = new EventEmitter<QrCodeRecord>();
  @Output() searchChange = new EventEmitter<string>();
  @Output() pageChange = new EventEmitter<number>();

  emitSearch(event: Event): void {
    this.searchChange.emit((event.target as HTMLInputElement).value);
  }
}
